"use client";

import React from "react";
import { motion } from "framer-motion";

/**
 * ScrollReveal Component for smooth scroll-triggered animations
 *
 * A reusable component that provides smooth scroll-triggered animations
 * for sections and components when they enter the viewport.
 *
 * Features:
 * - Fade in + slide up animation
 * - Triggers only once when element enters viewport
 * - Configurable animation properties
 * - Works with any children (MUI components, custom components, etc.)
 *
 * @param {React.ReactNode} children - The content to animate
 * @param {number} delay - Animation delay in seconds (default: 0)
 * @param {number} duration - Animation duration in seconds (default: 0.6)
 * @param {number} yOffset - Initial Y offset for slide animation (default: 40)
 * @param {number} viewportAmount - Percentage of element that must be visible to trigger (default: 0.2)
 * @param {string} ease - Animation easing function (default: "easeOut")
 */
const ScrollReveal = ({
  children,
  delay = 0,
  duration = 0.8,
  yOffset = 30,
  viewportAmount = 0.1,
  ease = "easeOut",
}) => {
  return (
    <motion.div
      // Initial state: hidden and positioned below
      initial={{
        opacity: 0,
        y: yOffset,
      }}
      // Animation when element enters viewport
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      // Animation transition settings - smoother and more gradual
      transition={{
        duration: duration,
        ease: [0.25, 0.1, 0.25, 1], // Custom cubic-bezier for smoother animation
        delay: delay,
      }}
      // Viewport settings: trigger when 10% of element is visible for earlier start
      viewport={{
        once: true,
        amount: viewportAmount,
        margin: "-50px 0px", // Start animation 50px before element enters viewport
      }}
      // Ensure the element takes full width/height of its container
      style={{ width: "100%" }}
    >
      {children}
    </motion.div>
  );
};

export default ScrollReveal;
